import { Info, Lock } from 'lucide-react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router';
import {
  definitionModule,
  estModule,
  estModuleAddon,
  type Module,
  type ModuleOptionnel,
} from '@artosera/shared';
import { modulesDeLaSession } from '../../auth/sujet.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { useShellContext } from '../shell/context.js';
import { familleDuModule } from './catalogue.js';
import {
  appuisActifs,
  avecLuiAuMemePalier,
  lienDuModuleActif,
  modulesAVenirDe,
  palierDuModule,
  type PalierAVenir,
} from './modulesAVenir.js';
import { SchemaIndicatif } from './SchemaIndicatif.js';
import { TuileModule } from './TuileModule.js';
import styles from './ModuleAVenirPage.module.css';

/**
 * Fiche d'un module À VENIR (`/:slug/modules/:id`) : ce qu'il sera, jamais ce
 * qu'il contient. Entièrement statique — textes de l'i18n, schéma générique —,
 * elle n'appelle AUCUNE route du module, ne monte aucun de ses écrans et ne
 * lance aucun travail.
 *
 *   - module à venir : la fiche ;
 *   - module déjà actif : on suit le lien vers sa page ;
 *   - module inconnu, add-on, ou d'un palier débloqué que la galerie n'a pas :
 *     page introuvable de la régie. Un module non acheté ne s'affiche pas.
 */
export function ModuleAVenirPage(): JSX.Element {
  const { id } = useParams();
  const { session, galerie, galerieSlug } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);

  if (!estModule(id) || estModuleAddon(id)) return <NotFoundPage />;
  const module: Module = id;

  if (modulesDeLaSession(galerie).includes(module)) {
    return (
      <Navigate replace to={lienDuModuleActif(galerie, galerieSlug, module) ?? `/${galerieSlug}`} />
    );
  }

  const avenir = modulesAVenirDe(session, galerie);
  if (!(avenir.modules as readonly Module[]).includes(module)) return <NotFoundPage />;
  const optionnel = module as ModuleOptionnel;

  const palier = palierDuModule(module) as PalierAVenir;
  const definition = definitionModule(module);
  const cle = `regie:modulesAVenir.fiches.${module}`;
  /** Les textes numérotés d'une fiche (`sert.1`, `sert.2`…), dans l'ordre. */
  const textes = (prefixe: string): string[] => {
    const liste: string[] = [];
    for (let n = 1; i18n.exists(`${cle}.${prefixe}.${n}`); n += 1)
      liste.push(t(`${cle}.${prefixe}.${n}`));
    return liste;
  };
  const sert = textes('sert');
  const fonctions = textes('fonctions');
  const avecLui = avecLuiAuMemePalier(avenir, optionnel);
  const appuis = appuisActifs(optionnel, galerie);
  const famille = familleDuModule(module);

  return (
    <div className={styles.page} data-testid="module-a-venir" data-module={module}>
      <nav className={styles.ariane} aria-label={t('regie:modulesAVenir.fiche.ariane')}>
        <Link to={`/${galerieSlug}#carte-des-modules`}>{t('regie:modulesAVenir.carte.titre')}</Link>
        <span aria-hidden="true">/</span>
        <span>{t(`regie:modulesAVenir.carte.familles.${famille}`)}</span>
        <span aria-hidden="true">/</span>
        <span aria-current="page" className={styles.courant}>
          {t(definition.libelle)}
        </span>
      </nav>

      <header className={styles.entete}>
        <span className={styles.badge}>
          <Lock size={14} strokeWidth={1.5} aria-hidden />
          {t(`regie:modulesAVenir.arrive.${palier}`)}
        </span>
        <h1 className={styles.titre}>{t(definition.libelle)}</h1>
        <p className={styles.usage}>{t(`${cle}.usage`)}</p>
      </header>

      <div className={styles.grille}>
        <div className={styles.pile}>
          <section className={styles.carte} aria-labelledby="fiche-a-quoi">
            <h2 id="fiche-a-quoi">{t('regie:modulesAVenir.fiche.aQuoi')}</h2>
            {sert.map((paragraphe) => (
              <p key={paragraphe}>{paragraphe}</p>
            ))}
          </section>

          <section className={styles.carte} aria-labelledby="fiche-faire">
            <h2 id="fiche-faire">{t('regie:modulesAVenir.fiche.faire')}</h2>
            <ol className={styles.fonctions}>
              {fonctions.map((fonction) => (
                <li key={fonction}>{fonction}</li>
              ))}
            </ol>
          </section>

          <div className={styles.note} role="note">
            <Info size={18} strokeWidth={1.5} aria-hidden />
            <p>
              <strong>{t('regie:modulesAVenir.fiche.noteTitre')}</strong>{' '}
              {t(
                `regie:modulesAVenir.fiche.${definition.famille === 'service' ? 'noteService' : 'note'}.${palier}`,
              )}
            </p>
          </div>
        </div>

        <aside className={styles.pile} aria-label={t('regie:modulesAVenir.fiche.complements')}>
          <div className={styles.carte}>
            <SchemaIndicatif />
          </div>

          {avecLui.length > 0 ? (
            <section className={styles.carte} aria-labelledby="fiche-avec-lui">
              <h2 id="fiche-avec-lui">{t(`regie:modulesAVenir.memePalier.${palier}`)}</h2>
              <ul className={styles.tuiles}>
                {avecLui.map((autre) => (
                  <li key={autre}>
                    <TuileModule
                      module={autre}
                      galerieSlug={galerieSlug}
                      etat="avenir"
                      palier={palier}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {appuis.length > 0 ? (
            <section className={styles.carte} aria-labelledby="fiche-appuie">
              <h2 id="fiche-appuie">{t('regie:modulesAVenir.fiche.sAppuie')}</h2>
              <ul className={styles.tuiles}>
                {appuis.map((appui) => (
                  <li key={appui}>
                    <TuileModule
                      module={appui}
                      galerieSlug={galerieSlug}
                      etat="actif"
                      to={lienDuModuleActif(galerie, galerieSlug, appui)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
